'use client';

import { Skeleton } from '@/components/ui/feedback/status/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/overlay/dialog';
import { cn } from '@/lib/ui/utils';
import { useTranslations } from 'next-intl';
import type { CmsAccountSettingsPresentation } from './cms-account-settings-dialog';
import {
  cmsAccountSettingsDialogContentClassName,
  cmsAccountSettingsDialogPositionerClassName,
} from './cms-account-settings-dialog-layout';

export function CmsAccountSettingsDialogLoading({
  presentation = 'dialog',
}: {
  presentation?: CmsAccountSettingsPresentation;
}) {
  const tCommon = useTranslations('Common');
  const loadingLabel = tCommon('loading');

  const loadingContent = (
    <>
      {presentation === 'dialog' ? (
        <>
          <DialogTitle className="sr-only">{loadingLabel}</DialogTitle>
          <DialogDescription className="sr-only">
            {loadingLabel}
          </DialogDescription>
        </>
      ) : null}

      <output
        aria-label={loadingLabel}
        className={cn(
          'grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)]',
          presentation === 'dialog' &&
            'sm:grid-cols-[14rem_minmax(0,1fr)] sm:grid-rows-1'
        )}
        data-account-settings-loading=""
        data-account-settings-presentation={presentation}
      >
        <aside
          aria-hidden="true"
          className={cn(
            'flex min-h-0 flex-col border-border/60 border-b',
            presentation === 'drawer'
              ? 'bg-transparent'
              : 'bg-[var(--cms-workspace-surface-control)] sm:border-r sm:border-b-0'
          )}
          data-account-settings-loading-sidebar=""
        >
          {presentation === 'dialog' ? (
            <div className="hidden items-center gap-3 px-4 pt-4 pb-3 sm:flex">
              <Skeleton className="size-9 shrink-0 rounded-control" />
              <Skeleton className="h-3 w-20 rounded-control" />
            </div>
          ) : null}

          <div
            className={cn(
              'min-w-0 gap-1 overflow-hidden px-3',
              presentation === 'drawer'
                ? 'grid grid-cols-4 py-2'
                : 'flex py-3 sm:flex-1 sm:flex-col sm:py-2'
            )}
          >
            {[0, 1, 2, 3].map((item) => (
              <div
                className={cn(
                  'flex items-center rounded-control py-2',
                  presentation === 'drawer'
                    ? 'min-h-11 min-w-0 justify-center gap-2 px-2'
                    : 'min-h-10 flex-1 gap-3 px-3 sm:flex-none'
                )}
                key={item}
              >
                <Skeleton
                  className={cn(
                    'shrink-0 rounded-pill',
                    presentation === 'drawer' ? 'size-5' : 'size-4'
                  )}
                />
                <Skeleton className="h-3 w-16 rounded-control" />
              </div>
            ))}
          </div>

          {presentation === 'dialog' ? (
            <div className="hidden border-border/60 border-t p-3 sm:block">
              <div className="flex min-h-10 items-center gap-3 px-3 py-2">
                <Skeleton className="size-4 shrink-0 rounded-pill" />
                <Skeleton className="h-3 w-14 rounded-control" />
              </div>
            </div>
          ) : null}
        </aside>

        <main
          aria-hidden="true"
          className="flex min-h-0 min-w-0 flex-col"
          data-account-settings-loading-main=""
        >
          <header
            className={cn(
              'flex items-start border-border/60 border-b',
              presentation === 'drawer'
                ? 'min-h-16 px-4 py-3'
                : 'min-h-20 px-5 py-4 sm:px-7'
            )}
          >
            <div className="space-y-2">
              <Skeleton className="h-5 w-24 rounded-control" />
              <Skeleton className="h-3 w-44 rounded-control" />
            </div>
          </header>

          <div
            className={cn(
              'min-h-0 flex-1',
              presentation === 'drawer'
                ? 'px-4 py-4'
                : 'px-4 py-5 sm:px-7 sm:py-6'
            )}
          >
            <div
              className={cn(
                'rounded-panel border border-border/65 bg-card',
                presentation === 'drawer'
                  ? 'space-y-4 p-4'
                  : 'space-y-6 p-6 sm:p-7'
              )}
              data-account-settings-loading-card=""
            >
              <div className="space-y-4">
                <Skeleton className="h-5 w-28 rounded-control" />
                <Skeleton className="h-10 w-full rounded-control" />
              </div>
              <div
                className={cn(
                  'flex items-center gap-4 border-border/60 border-t',
                  presentation === 'drawer' ? 'pt-4' : 'pt-6'
                )}
              >
                <Skeleton
                  className={cn(
                    'shrink-0 rounded-pill',
                    presentation === 'drawer' ? 'size-12' : 'size-14'
                  )}
                />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-32 rounded-control" />
                  <Skeleton className="h-3 w-48 max-w-full rounded-control" />
                </div>
              </div>
            </div>
          </div>
        </main>
      </output>
    </>
  );

  if (presentation === 'drawer') {
    return loadingContent;
  }

  return (
    <Dialog open={true}>
      <DialogContent
        className={cmsAccountSettingsDialogContentClassName}
        positionerClassName={cmsAccountSettingsDialogPositionerClassName}
        showCloseButton={false}
      >
        {loadingContent}
      </DialogContent>
    </Dialog>
  );
}
